export type TaskViewMode = 'list' | 'kanban' | 'calendar';

export type TaskHubStatus = 'todo' | 'in_progress' | 'done' | 'cancelled';

/** List-view grouping dimension (`?groupBy=`). Default `status`. */
export type TaskListGroupBy = 'status' | 'priority' | 'assignee' | 'dueBucket';

export const DEFAULT_TASK_LIST_GROUP_BY: TaskListGroupBy = 'status';

export const TASK_LIST_GROUP_BY_OPTIONS: readonly TaskListGroupBy[] = [
  'status',
  'priority',
  'assignee',
  'dueBucket',
];

export const isTaskListGroupBy = (value: string | null): value is TaskListGroupBy =>
  value !== null &&
  (TASK_LIST_GROUP_BY_OPTIONS as readonly string[]).includes(value);

const TASK_HUB_STATUSES: TaskHubStatus[] = [
  'todo',
  'in_progress',
  'done',
  'cancelled',
];

export const isTaskHubStatus = (value: string | null): value is TaskHubStatus =>
  value !== null && TASK_HUB_STATUSES.includes(value as TaskHubStatus);

/** Query param for the hub detail drawer. */
export const TASK_ID_PARAM = 'taskId';

/** Deep-link highlight target inside the task discussion panel. */
export const TASK_COMMENT_ID_PARAM = 'commentId';

/** Query param for the hub create drawer. */
export const TASK_CREATE_PARAM = 'create';

export const TASK_LEAD_ID_PARAM = 'leadId';
export const TASK_PATIENT_ID_PARAM = 'patientId';
/** Prefill status when opening create from a kanban column (not the hub status filter). */
export const TASK_CREATE_STATUS_PARAM = 'createStatus';
/** Prefill due date (`YYYY-MM-DD`) when opening create from a calendar day. */
export const TASK_CREATE_DUE_PARAM = 'createDue';

/** Default hub view when `?view=` is missing or invalid. */
export const DEFAULT_TASK_VIEW: TaskViewMode = 'list';

export const isTaskViewMode = (value: string | null): value is TaskViewMode =>
  value === 'list' || value === 'kanban' || value === 'calendar';

const isTruthyFlag = (value: string | null): boolean =>
  value === '1' || value === 'true' || value === 'yes';

export interface TaskHubScope {
  view: TaskViewMode;
  /** My Tasks — assignee = current user (any project). */
  mine: boolean;
  /** Tasks created by the current user (any project). */
  createdByMe: boolean;
  /** Selected project (Inbox or regular). Mutually exclusive with personal scopes in URLs we build. */
  projectId: string | null;
  /** Filter tasks to a single status (optional). */
  status: TaskHubStatus | null;
  /** Spotlight / filter: due on or before yesterday (open tasks). */
  overdue: boolean;
  /** Filter: due today … today+3 (open tasks). Mutually exclusive with overdue. */
  dueSoon: boolean;
}

const isDateKey = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

export interface TaskCreatePrefill {
  projectId?: string | null;
  leadId?: string | null;
  patientId?: string | null;
  /** Initial status for create dialog (e.g. from a kanban column). */
  status?: TaskHubStatus | null;
  /** Org-local due date `YYYY-MM-DD` (e.g. from a calendar day cell). */
  dueDate?: string | null;
  /** In-memory only — do not put message body on the URL. */
  title?: string | null;
  description?: string | null;
  /** Staff `_id`, not User id. */
  assigneeId?: string | null;
  sourceThreadId?: string | null;
  sourceMessageId?: string | null;
}

/** Parse Tasks hub scope from URL search params. */
export const parseTaskHubScope = (
  searchParams: URLSearchParams
): TaskHubScope => {
  const viewParam = searchParams.get('view');
  const view: TaskViewMode = isTaskViewMode(viewParam)
    ? viewParam
    : DEFAULT_TASK_VIEW;
  const projectIdRaw = searchParams.get('projectId')?.trim() || '';
  const projectId = projectIdRaw || null;
  const overdue = searchParams.get('overdue') === '1';
  const dueSoon = !overdue && searchParams.get('dueSoon') === '1';
  const createdByMe = isTruthyFlag(searchParams.get('createdByMe'));
  const statusRaw = searchParams.get('status')?.trim() || '';
  const status = isTaskHubStatus(statusRaw) ? statusRaw : null;

  // Explicit project wins over personal scopes when both present.
  if (projectId) {
    return {
      view,
      mine: false,
      createdByMe: false,
      projectId,
      status,
      overdue,
      dueSoon,
    };
  }

  if (createdByMe) {
    return {
      view,
      mine: false,
      createdByMe: true,
      projectId: null,
      status,
      overdue,
      dueSoon,
    };
  }

  const mineParam = searchParams.get('mine');
  const mineExplicitOff = mineParam === '0' || mineParam === 'false';
  // Urgency links omit mine and mean My Tasks. Explicit mine=0 (Tất cả dự án) stays org-wide.
  const mine =
    !mineExplicitOff &&
    (isTruthyFlag(mineParam) || mineParam == null || overdue || dueSoon);

  return {
    view,
    mine,
    createdByMe: false,
    projectId: null,
    status,
    overdue,
    dueSoon,
  };
};

/** True when URL already has a resolvable work scope (mine / createdByMe / projectId). */
export const hasExplicitTaskScope = (searchParams: URLSearchParams): boolean => {
  if (searchParams.get('projectId')?.trim()) return true;
  if (isTruthyFlag(searchParams.get('createdByMe'))) return true;
  const mine = searchParams.get('mine');
  return mine === '1' || mine === 'true' || mine === '0' || mine === 'false';
};

export const isTaskCreateOpen = (searchParams: URLSearchParams): boolean =>
  isTruthyFlag(searchParams.get(TASK_CREATE_PARAM));

export const parseTaskCreatePrefill = (
  searchParams: URLSearchParams
): TaskCreatePrefill => {
  const statusRaw = searchParams.get(TASK_CREATE_STATUS_PARAM)?.trim() || '';
  const dueRaw = searchParams.get(TASK_CREATE_DUE_PARAM)?.trim() || '';
  return {
    projectId: searchParams.get('projectId')?.trim() || null,
    leadId: searchParams.get(TASK_LEAD_ID_PARAM)?.trim() || null,
    patientId: searchParams.get(TASK_PATIENT_ID_PARAM)?.trim() || null,
    status: isTaskHubStatus(statusRaw) ? statusRaw : null,
    dueDate: isDateKey(dueRaw) ? dueRaw : null,
  };
};

const clearCreateParams = (params: URLSearchParams): void => {
  params.delete(TASK_CREATE_PARAM);
  params.delete(TASK_LEAD_ID_PARAM);
  params.delete(TASK_PATIENT_ID_PARAM);
  params.delete(TASK_CREATE_STATUS_PARAM);
  params.delete(TASK_CREATE_DUE_PARAM);
};

/** Build /tasks URL for view + Asana-style scope. */
export const buildTasksUrl = (options: {
  view?: TaskViewMode | string | null;
  mine?: boolean;
  createdByMe?: boolean;
  projectId?: string | null;
  status?: TaskHubStatus | null;
  overdue?: boolean;
  dueSoon?: boolean;
  taskId?: string | null;
  commentId?: string | null;
  create?: boolean;
  leadId?: string | null;
  patientId?: string | null;
  /** Prefill create dialog status (kanban column); distinct from hub `status` filter. */
  createStatus?: TaskHubStatus | null;
  /** Prefill create dialog due date (`YYYY-MM-DD`). */
  createDue?: string | null;
} = {}): string => {
  const params = new URLSearchParams();
  const view = isTaskViewMode(options.view ?? null)
    ? options.view!
    : DEFAULT_TASK_VIEW;
  params.set('view', view);

  const projectId = options.projectId?.trim() || '';
  if (projectId) {
    params.set('projectId', projectId);
  } else if (options.createdByMe) {
    params.set('createdByMe', '1');
  } else if (options.mine === false) {
    // Explicit org-wide / all-projects scope (not My Tasks).
    params.set('mine', '0');
  } else {
    // Default scope is My Tasks when mine is omitted.
    params.set('mine', '1');
  }

  if (options.overdue) {
    params.set('overdue', '1');
  } else if (options.dueSoon) {
    params.set('dueSoon', '1');
  }

  if (options.status && isTaskHubStatus(options.status)) {
    params.set('status', options.status);
  }

  const taskId = options.taskId?.trim() || '';
  if (taskId) {
    params.set(TASK_ID_PARAM, taskId);
    const commentId = options.commentId?.trim() || '';
    if (commentId) {
      params.set(TASK_COMMENT_ID_PARAM, commentId);
    }
  } else if (options.create) {
    params.set(TASK_CREATE_PARAM, '1');
    const leadId = options.leadId?.trim() || '';
    const patientId = options.patientId?.trim() || '';
    if (leadId) params.set(TASK_LEAD_ID_PARAM, leadId);
    if (patientId) params.set(TASK_PATIENT_ID_PARAM, patientId);
    if (options.createStatus && isTaskHubStatus(options.createStatus)) {
      params.set(TASK_CREATE_STATUS_PARAM, options.createStatus);
    }
    const createDue = options.createDue?.trim() || '';
    if (isDateKey(createDue)) {
      params.set(TASK_CREATE_DUE_PARAM, createDue);
    }
  }

  return `/tasks?${params.toString()}`;
};

/**
 * Set or clear taskId on existing search params (keeps view and other keys).
 * Setting a taskId clears create-mode params (mutual exclusion).
 */
export const withTaskIdParam = (
  searchParams: URLSearchParams,
  taskId: string | null
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  if (taskId) {
    next.set(TASK_ID_PARAM, taskId);
    next.delete(TASK_COMMENT_ID_PARAM);
    clearCreateParams(next);
  } else {
    next.delete(TASK_ID_PARAM);
    next.delete(TASK_COMMENT_ID_PARAM);
  }
  return next;
};

/**
 * Open or close create drawer on existing search params.
 * Opening create clears taskId (mutual exclusion).
 */
export const withCreateParam = (
  searchParams: URLSearchParams,
  create: boolean,
  prefill: TaskCreatePrefill = {}
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  if (create) {
    next.delete(TASK_ID_PARAM);
    next.delete(TASK_COMMENT_ID_PARAM);
    next.set(TASK_CREATE_PARAM, '1');
    const leadId = prefill.leadId?.trim() || '';
    const patientId = prefill.patientId?.trim() || '';
    if (leadId) next.set(TASK_LEAD_ID_PARAM, leadId);
    else next.delete(TASK_LEAD_ID_PARAM);
    if (patientId) next.set(TASK_PATIENT_ID_PARAM, patientId);
    else next.delete(TASK_PATIENT_ID_PARAM);
    if (prefill.status !== undefined) {
      if (prefill.status && isTaskHubStatus(prefill.status)) {
        next.set(TASK_CREATE_STATUS_PARAM, prefill.status);
      } else {
        next.delete(TASK_CREATE_STATUS_PARAM);
      }
    } else {
      next.delete(TASK_CREATE_STATUS_PARAM);
    }
    if (prefill.dueDate !== undefined) {
      const dueDate = prefill.dueDate?.trim() || '';
      if (isDateKey(dueDate)) {
        next.set(TASK_CREATE_DUE_PARAM, dueDate);
      } else {
        next.delete(TASK_CREATE_DUE_PARAM);
      }
    } else {
      next.delete(TASK_CREATE_DUE_PARAM);
    }
    // projectId is hub scope — leave as-is unless explicitly provided
    if (prefill.projectId !== undefined) {
      const projectId = prefill.projectId?.trim() || '';
      if (projectId) {
        next.set('projectId', projectId);
        next.delete('mine');
        next.delete('createdByMe');
      }
    }
  } else {
    clearCreateParams(next);
  }
  return next;
};

/** Close both create and detail drawers (keeps view/scope). */
export const withoutDrawerParams = (
  searchParams: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  next.delete(TASK_ID_PARAM);
  next.delete(TASK_COMMENT_ID_PARAM);
  clearCreateParams(next);
  return next;
};

export type TaskOwnershipFilter = '' | 'mine' | 'createdByMe';

/**
 * Set Việc của tôi / Việc tôi tạo on the hub URL.
 * A project scope keeps its own params; ownership then lives in filter state.
 */
export const withOwnershipParams = (
  searchParams: URLSearchParams,
  ownership: TaskOwnershipFilter
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  if (next.get('projectId')?.trim()) return next;
  next.delete('mine');
  next.delete('createdByMe');
  if (ownership === 'createdByMe') {
    next.set('createdByMe', '1');
  } else if (ownership === 'mine') {
    next.set('mine', '1');
  } else {
    next.set('mine', '0');
  }
  return next;
};

/**
 * Set or clear overdue / dueSoon flags on existing search params.
 * Mutual exclusive; empty clears both.
 */
export const withDueFocusParams = (
  searchParams: URLSearchParams,
  dueFocus: '' | 'overdue' | 'dueSoon'
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  next.delete('overdue');
  next.delete('dueSoon');
  if (dueFocus === 'overdue') {
    next.set('overdue', '1');
  } else if (dueFocus === 'dueSoon') {
    next.set('dueSoon', '1');
  }
  return next;
};

/** Parse list groupBy from URL; invalid / missing → status. */
export const parseTaskListGroupBy = (
  searchParams: URLSearchParams
): TaskListGroupBy => {
  const raw = searchParams.get('groupBy')?.trim() || '';
  return isTaskListGroupBy(raw) ? raw : DEFAULT_TASK_LIST_GROUP_BY;
};

/** Set or clear groupBy (omit param when default status). */
export const withGroupByParam = (
  searchParams: URLSearchParams,
  groupBy: TaskListGroupBy
): URLSearchParams => {
  const next = new URLSearchParams(searchParams);
  if (groupBy === DEFAULT_TASK_LIST_GROUP_BY) {
    next.delete('groupBy');
  } else {
    next.set('groupBy', groupBy);
  }
  return next;
};
